import React, { ReactNode, useContext, useMemo } from 'react';
import { ListingPackage, ListingStage } from 'models/Listing';
import ResolvePopup from 'components/ResolvePopup';
import Tag from 'components/ListingPortfolioCard/components/Tag';
import { useTranslation } from 'react-i18next';
import { COLOR_VARIANT } from 'models/Colors';
import {
  BUYERS_ACTIVITY_TRACK_EVENTS_COMPACT_COPY,
  BuyersActivityTrackEvents,
} from 'models/Lead';
import useViewPortfolioAs from 'hooks/useViewPortfolioAs';
import { LISTING_PACKAGE_DATA } from 'router/subrouters/ListingCreationWizard/common-pages/ListingOptions/components/ListingOptionCardField/components/ListingOptionCard/data';
import { LISTING_STATUS_COLOR, LISTING_STATUS_LABEL } from 'data/dealStatus';
import {
  DATE_FORMAT_COMPACT_SLASH,
  getFormattedDate,
  getListingExpirationDate,
} from 'dateUtils';
import { Button } from 'ncoded-component-library';
import { checkCanProlongListing, checkIsEnded } from 'utils';
import AuthContext from 'providers/Auth/Auth.context';
import { useLocation } from 'react-router-dom';

export type ActivityMetricsTableData = {
  _id: string;

  dealId: string;
  dealName: string;
  dealCreatedAt: Date;

  hasListing: boolean;
  listingStatus: ListingStage;
  listingPackage?: ListingPackage | null;
  listingPublishedAt?: Date;
  listingExpiresAt?: Date;
  seekingExpressionOfInterestDate?: Date;

  latestLeadAt?: Date;
  totalLeadsCount: number;

  latestNoteAt?: Date;
  dealNotesCount: number;
  leadNotesCount: number;

  brokerId: string;
  brokerName: string;
  brokerageFirmName?: string;
  brokerageFirmBrokersCount: number;

  messageChannelsCount: number;
  filesCount: number;

  latestActivityAt?: Date;
  latestActivityAction?: BuyersActivityTrackEvents;
};

// Define table columns
export const COLUMNS: Array<keyof ActivityMetricsTableData> = [
  'dealName',
  'dealCreatedAt',
  'hasListing',
  'listingStatus',
  'latestLeadAt',
  'latestNoteAt',
  'brokerName',
  'brokerageFirmName',
  'messageChannelsCount',
  'filesCount',
  'latestActivityAt',
];

// Define heading translations
export const HEADING_TRANSLATIONS: Partial<
  Record<typeof COLUMNS[number], string>
> = {
  dealName: 'deal',
  dealCreatedAt: 'dealCreationDate',
  hasListing: 'listingAdded',
  listingStatus: 'listingStatus',
  latestLeadAt: 'latestLead',
  latestNoteAt: 'latestNote',
  brokerName: 'broker',
  brokerageFirmName: 'brokerageFirm',
  messageChannelsCount: 'messageChannels',
  filesCount: 'filesCount',
  latestActivityAt: 'latestActivity',
};

// Define sorting values (optional)
export const SORTING_VALUES: Partial<Record<typeof COLUMNS[number], string>> = {
  dealName: 'dealName',
  dealCreatedAt: 'dealCreatedAt',
  hasListing: 'hasListing',
  listingStatus: 'listingStatus',
  latestLeadAt: 'latestLeadAt',
  latestNoteAt: 'latestNoteAt',
  brokerName: 'brokerName',
  brokerageFirmName: 'brokerageFirmName',
  messageChannelsCount: 'messageChannelsCount',
  filesCount: 'filesCount',
  latestActivityAt: 'latestActivityAt',
};

// Define custom column sizes (optional)
export const CUSTOM_COL_SIZES: Partial<Record<typeof COLUMNS[number], number>> =
  { dealName: 300 };

type CellProps = {
  data: ActivityMetricsTableData;
};

const NameCell: React.FC<CellProps> = ({ data }) => {
  const { dealName } = data;

  return <div className="as-link-black">{dealName}</div>;
};

const DealCreatedAtCell: React.FC<CellProps> = ({ data }) => {
  const { dealCreatedAt } = data;

  return (
    <div>{getFormattedDate(dealCreatedAt, DATE_FORMAT_COMPACT_SLASH)}</div>
  );
};

const ListingAddedCell: React.FC<CellProps> = ({ data }) => {
  const { hasListing, listingPackage } = data;

  const { t } = useTranslation();

  return (
    <div>
      <ResolvePopup
        onHover
        popupContent={t(
          hasListing
            ? t(LISTING_PACKAGE_DATA[listingPackage]?.title)
            : 'noPaidPackage',
        )}
      >
        <Tag
          variant={COLOR_VARIANT[hasListing ? 'GREEN' : 'ORANGE']}
          content={t(hasListing ? 'yes' : 'no')}
        />
      </ResolvePopup>
    </div>
  );
};

const ListingStatusCell: React.FC<CellProps> = ({ data }) => {
  const {
    listingStatus,
    listingPublishedAt,
    listingExpiresAt,
    seekingExpressionOfInterestDate,
  } = data;

  const exprDate = useMemo(
    () =>
      getListingExpirationDate(
        listingExpiresAt,
        seekingExpressionOfInterestDate,
      ),
    [listingExpiresAt, seekingExpressionOfInterestDate],
  );

  const { t } = useTranslation();

  if (!listingStatus) return <div>{t('na')}</div>;

  const isExpired = checkIsEnded(listingStatus);

  const tooltipContent =
    !listingPublishedAt && !exprDate ? null : (
      <div>
        {listingPublishedAt && (
          <div>
            {t('publishedOn')}{' '}
            {getFormattedDate(listingPublishedAt, DATE_FORMAT_COMPACT_SLASH)}
          </div>
        )}
        {checkCanProlongListing(listingStatus) && exprDate && (
          <div>
            {t(isExpired ? 'expiredOn' : 'expiresOn')}{' '}
            {getFormattedDate(exprDate, DATE_FORMAT_COMPACT_SLASH)}
          </div>
        )}
      </div>
    );

  const RenderedTag = (
    <Tag
      variant={LISTING_STATUS_COLOR[listingStatus]}
      content={t(LISTING_STATUS_LABEL[listingStatus])}
    />
  );

  return (
    <div>
      {!tooltipContent ? (
        RenderedTag
      ) : (
        <ResolvePopup onHover popupContent={tooltipContent}>
          {RenderedTag}
        </ResolvePopup>
      )}
    </div>
  );
};

const LatestLeadCell: React.FC<CellProps> = ({ data }) => {
  const { latestLeadAt, totalLeadsCount } = data;

  const { t } = useTranslation();

  if (!latestLeadAt) return <div>{t('na')}</div>;

  return (
    <div>
      <ResolvePopup
        onHover
        popupContent={t('leadsInTotal', { count: totalLeadsCount })}
      >
        <span>{getFormattedDate(latestLeadAt, DATE_FORMAT_COMPACT_SLASH)}</span>
      </ResolvePopup>
    </div>
  );
};

const LatestNoteCell: React.FC<CellProps> = ({ data }) => {
  const { latestNoteAt, dealNotesCount, leadNotesCount } = data;

  const { t } = useTranslation();

  if (!latestNoteAt) return <div>{t('na')}</div>;

  const dealNotes = t('dealNotes', { count: dealNotesCount });
  const leadNotes = t('leadNotes', { count: leadNotesCount });

  return (
    <div>
      <ResolvePopup
        onHover
        popupContent={t('dealNotesAndLeadNotes', {
          dealNotes,
          leadNotes,
        })}
      >
        <span>{getFormattedDate(latestNoteAt, DATE_FORMAT_COMPACT_SLASH)}</span>
      </ResolvePopup>
    </div>
  );
};

const BrokerCell: React.FC<CellProps> = ({ data }) => {
  const { brokerId, brokerName } = data;

  const { updateLatestActivityMetricsLocation } = useContext(AuthContext);

  const location = useLocation();

  const navigateToUsersPortfolio = useViewPortfolioAs();

  const { t } = useTranslation();

  if (!brokerName) return <div>{t('na')}</div>;

  return (
    <Button
      style={{ padding: 0, textAlign: 'left' }}
      variant="link"
      className="as-link-black"
      onClick={(e) => {
        e.stopPropagation();
        updateLatestActivityMetricsLocation(location);
        navigateToUsersPortfolio(brokerId, brokerName);
      }}
    >
      {brokerName}
    </Button>
  );
};

const BrokerageFirmCell: React.FC<CellProps> = ({ data }) => {
  const { brokerageFirmName, brokerageFirmBrokersCount } = data;

  const { t } = useTranslation();

  if (!brokerageFirmName) return <div>{t('na')}</div>;

  return (
    <div>
      <ResolvePopup
        onHover
        popupContent={t('brokersCount', { count: brokerageFirmBrokersCount })}
      >
        <span>{brokerageFirmName}</span>
      </ResolvePopup>
    </div>
  );
};

const MessageChannelsCell: React.FC<CellProps> = ({ data }) => {
  const { messageChannelsCount } = data;

  return <div>{messageChannelsCount}</div>;
};

const FilesCell: React.FC<CellProps> = ({ data }) => {
  const { filesCount } = data;

  return <div>{filesCount}</div>;
};

const LatestActivityCell: React.FC<CellProps> = ({ data }) => {
  const { latestActivityAt, latestActivityAction } = data;

  const { t } = useTranslation();

  if (!latestActivityAt) return <div>{t('na')}</div>;

  return (
    <div>
      <ResolvePopup
        onHover
        popupContent={t('latestActivityWas', {
          activity: t(
            BUYERS_ACTIVITY_TRACK_EVENTS_COMPACT_COPY[latestActivityAction],
          ),
        })}
      >
        <span>
          {getFormattedDate(latestActivityAt, DATE_FORMAT_COMPACT_SLASH)}
        </span>
      </ResolvePopup>
    </div>
  );
};

export const BODY_CONTENT_GENERATE: Record<
  keyof ActivityMetricsTableData,
  (cellProps: CellProps) => ReactNode
> = {
  _id: null,
  dealId: null,
  dealName: (cellProps: CellProps) => <NameCell {...cellProps} />,
  dealCreatedAt: (cellProps: CellProps) => <DealCreatedAtCell {...cellProps} />,
  hasListing: (cellProps: CellProps) => <ListingAddedCell {...cellProps} />,
  listingPackage: null,
  listingStatus: (cellProps: CellProps) => <ListingStatusCell {...cellProps} />,
  listingPublishedAt: null,
  listingExpiresAt: null,
  seekingExpressionOfInterestDate: null,
  latestLeadAt: (cellProps: CellProps) => <LatestLeadCell {...cellProps} />,
  totalLeadsCount: null,
  latestNoteAt: (cellProps: CellProps) => <LatestNoteCell {...cellProps} />,
  dealNotesCount: null,
  leadNotesCount: null,
  brokerId: null,
  brokerName: (cellProps: CellProps) => <BrokerCell {...cellProps} />,
  brokerageFirmName: (cellProps: CellProps) => (
    <BrokerageFirmCell {...cellProps} />
  ),
  brokerageFirmBrokersCount: null,
  messageChannelsCount: (cellProps: CellProps) => (
    <MessageChannelsCell {...cellProps} />
  ),
  filesCount: (cellProps: CellProps) => <FilesCell {...cellProps} />,
  latestActivityAt: (cellProps: CellProps) => (
    <LatestActivityCell {...cellProps} />
  ),
  latestActivityAction: null,
};
